import { FC, KeyboardEvent, useMemo, useRef, useState } from 'react'

import { useAtom, useAtomValue } from 'jotai'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'
import {
  Text,
  GreyGrey20,
  GreyGrey60,
  Primary14ExtendedRegular,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  activeVersionIdAtom,
  automationNameAtom,
  editingVersionIdAtom,
  isEditingDisabledAtom,
  versionPreviewIdAtom
} from 'Automations/automationEditor/atoms'
import { UNTITLED_AUTOMATION_NAME } from 'Automations/automationEditor/constants'
import { PublishedBadge, PublishedBadgeStatus } from 'Automations/components'

export const Title: FC = () => {
  return (
    <Flex
      data-testid="automations.editor.titleBar"
      alignItems="center"
      justifyContent="center"
      position="absolute"
      left="50%"
      transform="translateX(-50%)"
      maxWidth="50%"
      gap="12px"
    >
      <Flex
        alignItems="center"
        data-testid="automations.editor.title"
        {...Primary14ExtendedRegular}
        color={GreyGrey60}
      >
        <Text as="span" color={GreyGrey60}>
          {'Workflows / '}
        </Text>
        <EditableName />
      </Flex>

      <Badge />
    </Flex>
  )
}

const EditableName: FC = () => {
  const [name, setName] = useAtom(automationNameAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const formattedName = name || UNTITLED_AUTOMATION_NAME
  const [isEditing, setIsEditing] = useState(false)
  // The name before the edit started, so Escape can revert the change
  const nameBeforeEdit = useRef(name)

  const startEditing = () => {
    if (isEditingDisabled) return
    nameBeforeEdit.current = name
    setIsEditing(true)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsEditing(false)
    } else if (event.key === 'Escape') {
      setName(nameBeforeEdit.current)
      setIsEditing(false)
    }
  }

  // Both the label and the input share these styles so toggling between them
  // never changes the name's size or position. No `color` is set so the name
  // keeps Chakra's default (dark) text colour — the prominent breadcrumb leaf,
  // contrasting with the muted grey "Workflows /" prefix.
  const pillStyles = {
    ...Primary14ExtendedRegular,
    paddingInline: '4px',
    paddingBlock: '2px',
    borderRadius: '6px'
  }

  return (
    <Box position="relative" display="inline-block">
      {/*
        An invisible, in-flow sizer keeps the wrapper exactly as wide as the
        content. The label/input are absolutely positioned on top of it, so the
        field grows with its value and the name never jumps when entering or
        leaving edit mode — and the input never inflates the wrapper width.
        While editing, the empty name falls back to the placeholder width.
      */}
      <Box {...pillStyles} aria-hidden visibility="hidden" whiteSpace="pre">
        {formattedName}
      </Box>

      {isEditing ? (
        <StyledNameInput
          data-testid="automations.editor.title.input"
          value={name}
          placeholder={UNTITLED_AUTOMATION_NAME}
          autoFocus
          onFocus={event => event.currentTarget.select()}
          onChange={event => setName(event.target.value)}
          onBlur={() => setIsEditing(false)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <Text
          {...pillStyles}
          data-testid="automations.editor.title.name"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          whiteSpace="pre"
          cursor={isEditingDisabled ? 'default' : 'text'}
          onClick={startEditing}
          _hover={
            isEditingDisabled ? undefined : { backgroundColor: GreyGrey20 }
          }
        >
          {formattedName}
        </Text>
      )}
    </Box>
  )
}

const Badge: FC = () => {
  const editingVersionId = useAtomValue(editingVersionIdAtom)
  const versionPreviewId = useAtomValue(versionPreviewIdAtom)
  const activeVersionId = useAtomValue(activeVersionIdAtom)

  const status = useMemo<PublishedBadgeStatus>(() => {
    // The badge reflects the version currently shown on the canvas — the
    // previewed version when previewing history, otherwise the editing version.
    const shownVersionId = versionPreviewId ?? editingVersionId
    const hasActiveVersion = !!activeVersionId
    const isShowingActiveVersion =
      hasActiveVersion && shownVersionId === activeVersionId

    return match({ isShowingActiveVersion, hasActiveVersion })
      .with({ isShowingActiveVersion: true }, () => 'published' as const)
      .with({ hasActiveVersion: true }, () => 'unpublishedChanges' as const)
      .otherwise(() => 'unpublished' as const)
  }, [editingVersionId, versionPreviewId, activeVersionId])

  return <PublishedBadge status={status} />
}

const StyledNameInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding-inline: 4px;
  padding-block: 2px;
  border: none;
  border-radius: 6px;
  background: ${GreyGrey20};
  ${css(Primary14ExtendedRegular)}

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: ${GreyGrey60};
  }
`
